import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import PageTransition from '@components/PageTransition/ui/PageTransition';

// material-ui
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import useMediaQuery from '@mui/material/useMediaQuery';

// project imports
import LinearProgressLoader from '@components/common/LinearProgressLoader';
import useConfig from '@hooks/useConfig';
import { handlerDrawerOpen, useGetMenuMaster } from '@api/menu';
import { AppBar, Toolbar } from '@mui/material';
import Header from '@layout/MainLayout/Header';
import { useDashboardSelector } from '@hooks/useDashboard';
import PageHeader from '@components/PageHeader/PageHeader';
import { usePageContext } from '@providers/PageMetaProvider/model/PageContext';
import NotificationDrawer from '@layout/MainLayout/NotificationSection/NotificationDrawer';
import DemandGeneratingModal from '@components/DemandGeneratingModal';
import { Toaster } from 'react-hot-toast';
import TemplateGeneratingModal from '@components/TemplateGeneratingModal';
import TourGuide from '@layout/MainLayout/TourGuide';
import TourGuideInitialModal from '@layout/MainLayout/TourGuide/InitialModal';
import { updateTourGuide } from '@api/user';
import DepositionSummaryGeneratingModal from '@components/DepositionSummaryGeneratingModal';
import UseCaseGeneratingModal from '@components/UseCaseGeneratingModal';
import Sidebar from './Sidebar';
import MainContentStyled from './MainContentStyled';

function MainLayout() {
  const theme = useTheme();
  const downMD = useMediaQuery(theme.breakpoints.down('md'));
  const { values } = usePageContext();
  const { container, miniDrawer } = useConfig();
  const isNotificationOpened = useDashboardSelector(
    (state) => state.isNotificationOpened
  );
  const location = useLocation();
  const { menuMaster, menuMasterLoading } = useGetMenuMaster();
  const drawerOpen = menuMaster?.isDashboardDrawerOpened;
  const [isTourRunning, setIsTourRunning] = useState(false);
  const [showTourModal, setShowTourModal] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    handlerDrawerOpen(!miniDrawer);
  }, [miniDrawer]);

  useEffect(() => {
    downMD && handlerDrawerOpen(false);
  }, [downMD]);

  // Safety net: if a modal/dialog unmounts abruptly, MUI (or other overlays) can leave the body scroll-locked.
  // On route changes, force-unlock scroll *only if* there are no active modals in the DOM.
  useEffect(() => {
    const cleanup = () => {
      const hasMuiModal = !!document.querySelector('.MuiModal-root');
      const modalRoot = document.getElementById('modalRoot');
      const hasPortalModal = !!modalRoot && modalRoot.childElementCount > 0;
      if (hasMuiModal || hasPortalModal) return;

      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.documentElement.style.overflow = '';
    };

    // Run after paint so any new route modals can mount first.
    const t = window.setTimeout(cleanup, 0);
    return () => window.clearTimeout(t);
  }, [location.pathname]);

  if (menuMasterLoading) return <LinearProgressLoader />;

  // Start the Joyride tour
  const startTour = () => {
    setIsTourRunning(true);
    setShowTourModal(false);
    updateTourGuide();
  };

  const skipTour = () => {
    setIsTourRunning(false);
    setShowTourModal(false);
    updateTourGuide();
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <TourGuide
        isTourRunning={isTourRunning}
        stepIndex={stepIndex}
        setStepIndex={setStepIndex}
        setIsTourRunning={setIsTourRunning}
        setShowTourModal={setShowTourModal}
      />

      {downMD && (
        <AppBar
          enableColorOnDark
          position="fixed"
          color="inherit"
          elevation={0}
          // Fixed, so a printout repeated it on every page.
          sx={{ bgcolor: 'background.default', displayPrint: 'none' }}
        >
          <Toolbar sx={{ p: 2 }}>
            <Header />
          </Toolbar>
        </AppBar>
      )}

      {/* Sidebar */}
      <Sidebar />

      {/* Notification Drawer */}
      {isNotificationOpened && <NotificationDrawer />}

      {/* Main Content */}
      <MainContentStyled
        {...{ open: drawerOpen, fullPage: values?.fullPage, theme }}
      >
        <Container
          maxWidth={container ? 'lg' : false}
          {...(!container && { sx: { px: { xs: 0 } } })}
        >
          {!values?.hideTop && <PageHeader />}
          <PageTransition />
          <DemandGeneratingModal />
          <TemplateGeneratingModal />
          <DepositionSummaryGeneratingModal />
          <UseCaseGeneratingModal />

          <TourGuideInitialModal
            showTourModal={showTourModal}
            startTour={startTour}
            skipTour={skipTour}
          />
        </Container>
      </MainContentStyled>

      {/* ZLAYT-1578: toasts are fixed to the window, so an open one printed
          over the report on every page. */}
      <Box sx={{ displayPrint: 'none' }}>
        <Toaster position="top-right" />
      </Box>
    </Box>
  );
}

export default MainLayout;
